import type { EmailScoreResult } from '../utils/emailScoring';

const GRADE_COLORS: Record<EmailScoreResult['grade'], string> = {
  A: 'text-brand-teal stroke-brand-teal',
  B: 'text-blue-500 stroke-blue-500',
  C: 'text-yellow-500 stroke-yellow-500',
  D: 'text-orange-500 stroke-orange-500',
  F: 'text-red-500 stroke-red-500',
};

const GRADE_TRACK: Record<EmailScoreResult['grade'], string> = {
  A: 'stroke-brand-teal-50',
  B: 'stroke-blue-50',
  C: 'stroke-yellow-50',
  D: 'stroke-orange-50',
  F: 'stroke-red-50',
};

interface EmailScorePanelProps {
  score: EmailScoreResult;
}

function CircularProgress({ value, grade }: { value: number; grade: EmailScoreResult['grade'] }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  const colorClass = GRADE_COLORS[grade];
  const trackClass = GRADE_TRACK[grade];

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width="136" height="136" viewBox="0 0 136 136" className="-rotate-90">
        <circle
          cx="68"
          cy="68"
          r={radius}
          fill="none"
          strokeWidth="10"
          className={trackClass}
        />
        <circle
          cx="68"
          cy="68"
          r={radius}
          fill="none"
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={`${colorClass} transition-all duration-500`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-bold ${colorClass.split(' ')[0]}`}>{grade}</span>
        <span className="text-sm text-neutral-600">{value}/100</span>
      </div>
    </div>
  );
}

function CheckIcon({ passed }: { passed: boolean }) {
  if (passed) {
    return (
      <svg className="h-5 w-5 flex-shrink-0 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    );
  }
  return (
    <svg className="h-5 w-5 flex-shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

export function EmailScorePanel({ score }: EmailScorePanelProps) {
  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <div className="border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
        <h3 className="text-sm font-semibold text-neutral-700">Punteggio Template</h3>
        <p className="text-xs text-neutral-600">Valutazione qualità del template email</p>
      </div>

      <div className="flex justify-center py-6">
        <CircularProgress value={score.totalScore} grade={score.grade} />
      </div>

      <div className="divide-y divide-neutral-100 border-t border-neutral-100">
        {score.checks.map((check) => (
          <div key={check.id} className="px-5 py-3">
            <div className="flex items-center gap-3">
              <CheckIcon passed={check.passed} />
              <span className="flex-1 text-sm text-neutral-700">{check.label}</span>
              <span
                className={`text-xs font-medium ${
                  check.passed ? 'text-brand-teal' : 'text-neutral-600'
                }`}
              >
                {check.score}/{check.maxScore}
              </span>
            </div>
            {!check.passed && check.tip && (
              <p className="mt-1 pl-8 text-xs text-neutral-600">{check.tip}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
